import React, { useCallback, useEffect, useState } from 'react';

import { BookingConfirmed } from './components/booking-confirmed';
import { ReceiptContent } from './components/receipt-content';
import { SwipeModalContent } from './components/swipe-modal-content';
import { ReceiptDeciding } from './components/receipt-deciding';
import { StyledModalWrapper } from './styles';
import { ADD_ONS_DECLINED_PARAM, useBookingAddOnsOffer } from 'hooks/use-booking-add-ons-offer.hook';
import { useReceipt } from './use-receipt.hook';
import { useIsActivityPane } from 'hooks/use-is-activity-pane.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useActivityPaneStyles } from 'hooks/payment-content/use-activity-pane-styles.hook';

interface ReceiptProps {
  transactionId?: number | string;
  onCloseReceipt: VoidFunction;
  isCancelBookingStep: boolean;
  setIsCancelBookingStep: React.Dispatch<React.SetStateAction<boolean>>;
  isFullscreen?: boolean;
  /*
   * RES-1326 — supplied only by the mount that HAS an `add-on-request` sibling to navigate to. Absent
   * from the `receipt/:transactionUuid` deep link, which renders this outside any `PaymentContent`, and
   * the add-ons prompt withholds itself when it is missing. See `AddOnsPrompt`.
   */
  onBrowseAddOns?: (bookingId: string) => void;
}

export const Receipt = ({
  transactionId,
  onCloseReceipt,
  isCancelBookingStep,
  setIsCancelBookingStep,
  isFullscreen = false,
  onBrowseAddOns,
}: ReceiptProps) => {
  const {
    isSwipeModalContent,
    onCancelBookingClick,
    patchTransactionData,
    transaction,
    onCheckInClick,
    isDeepLinkAccess,
  } = useReceipt({ transactionId, setIsCancelBookingStep });
  // Activity-center pane (VIS-2032): edge padding (global, portal-immune) + the fill on DoublePane.
  const isActivityPane = useIsActivityPane();
  useActivityPaneStyles(isActivityPane);
  useEffect(() => {
    if (isDeepLinkAccess && !transaction) {
      onCloseReceipt();
    }
  }, [transaction, isDeepLinkAccess, onCloseReceipt]);

  /*
   * A room that offers add-ons gets its own confirmation screen before the receipt.
   *
   * A local toggle, NOT a route: `pathname` is load-bearing here — the add-on step rewrites it by
   * substring, payment-content path-tests it to hide the native header, and PaymentContent is mounted
   * at nine sites. A sibling route was rejected on RES-1057 for those reasons.
   */
  const { shouldOffer, bookingId, isDeciding } = useBookingAddOnsOffer(transaction);
  const searchParams = useSearchParams<Record<string, string | undefined>>();
  const [showFullReceipt, setShowFullReceipt] = useState(false);
  const openFullReceipt = useCallback(() => setShowFullReceipt(true), []);

  /*
   * The pressed tile unmounts with the screen, so focus must move or it falls to <body>.
   *
   * Queried rather than threaded as a ref: both receipt bodies are shared with eight other mounts and
   * the deep link, and widening their props for one transition is the worse trade. Guarded on
   * `showFullReceipt` so an ordinary receipt arrival keeps its focus.
   */
  useEffect(() => {
    if (!showFullReceipt) {
      return;
    }

    /* From the document, not the receipt root: ReceiptContent PORTALS out of it. */

    /*
     * The status line, not a heading element: both receipt bodies render their title through a styled
     * div, so an `h1, h2` query matches nothing and focus falls to an unnamed container.
     * `booking-status-text` carries the line that names the screen.
     *
     * Not promoted to an <h1> instead: ReceiptOverview renders at nine mounts and on the deep link.
     */
    const target =
      document.querySelector<HTMLElement>('[data-testid="booking-status-text"]') ??
      document.querySelector<HTMLElement>('[data-testid="receipt-page"], [data-testid="receipt-swipe-modal"]');

    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus();
    }
  }, [showFullReceipt]);

  /*
   * Only for a booking just made. My-bookings and the landing page open a tapped booking through this
   * same route, passing it as `transactionId` — they hold it in state only a tile press sets, so its
   * presence means "I asked for this one". Arriving from checkout leaves it undefined.
   */
  const isJustBooked = !transactionId;

  /*
   * Latched, because the wait is bounded and a late success must not swap the screen under the tenant.
   *
   * Latched PER BOOKING, though: the displayed booking can change under an open receipt, and an answer
   * carried across would offer add-ons for a booking that does not qualify and send the tenant to a
   * step that rejects them. The id is part of the latch, so a different booking re-decides.
   */
  const [decided, setDecided] = useState<{ uuid?: string; offer: boolean } | null>(null);
  const decidedOffer = decided && decided.uuid === transaction?.uuid ? decided.offer : null;

  useEffect(() => {
    if (decidedOffer === null && !isDeciding) {
      setDecided({ uuid: transaction?.uuid, offer: shouldOffer });
    }
  }, [decidedOffer, isDeciding, shouldOffer, transaction?.uuid]);

  /*
   * Kept apart because they fail for different reasons: the first is about the booking, the second
   * about what the tenant has done since. `onBrowseAddOns` stays in the guards — it narrows the
   * optional prop for the component below.
   */
  const qualifiesForConfirmation = isJustBooked && decidedOffer && Boolean(bookingId);
  /*
   * ⛔ DECLINING COUNTS. "Continue without add-ons" leaves the step with nothing ordered, so the
   * booking is still eligible and the offer re-qualifies — putting the card the tenant just declined
   * straight back in front of them, with no way forward but to decline it again. The marker travels
   * on the navigation because both screens unmount in between and there is nothing left to hold it.
   */
  const declinedAddOns = searchParams?.[ADD_ONS_DECLINED_PARAM] === 'true';
  const supersededByTenant = showFullReceipt || isCancelBookingStep || declinedAddOns;

  /*
   * Both screens must bring their own modal on desktop. ReceiptBooking's desktop branch is a bare div,
   * so anything without one renders INLINE in whichever host PaymentContent sits in — the receipt
   * bodies escape it only because ReceiptContent renders its own ModalWrapper.
   *
   * Never on mobile: the SwipeModal is already an overlay and a second wrapper is two backdrops.
   */
  const asDesktopModal = (content: JSX.Element): JSX.Element =>
    isSwipeModalContent ? (
      content
    ) : (
      /*
       * ⛔ `withExitButton` is not optional. ModalWrapper defaults it off, so this modal shipped with no
       * corner control at all — the only way out was the "Done" button at the bottom of the content,
       * and the deciding state below has no footer, so for up to three seconds there was none.
       */
      <StyledModalWrapper visible withExitButton onClose={onCloseReceipt} content={content} />
    );

  if (onBrowseAddOns && isJustBooked && isDeciding) {
    return asDesktopModal(<ReceiptDeciding />);
  }

  if (onBrowseAddOns && qualifiesForConfirmation && !supersededByTenant) {
    return asDesktopModal(
      <BookingConfirmed
        booking={transaction}
        bookingId={bookingId}
        onOpenReceipt={openFullReceipt}
        onBrowseAddOns={onBrowseAddOns}
        onClose={onCloseReceipt}
      />,
    );
  }

  return isSwipeModalContent ? (
    <SwipeModalContent
      onCloseClick={onCloseReceipt}
      transaction={transaction}
      onCancelBookingClick={onCancelBookingClick}
      patchTransactionData={patchTransactionData}
      isSwipeModalContent={isSwipeModalContent}
      isCancelBookingStep={isCancelBookingStep}
      setIsCancelBookingStep={setIsCancelBookingStep}
      onCheckInClick={onCheckInClick}
      isFullscreen={isFullscreen}
      isActivityPane={isActivityPane}
    />
  ) : (
    <ReceiptContent
      transaction={transaction}
      onCancelBookingClick={onCancelBookingClick}
      patchTransactionData={patchTransactionData}
      onCloseClick={onCloseReceipt}
      isCancelBookingStep={isCancelBookingStep}
      setIsCancelBookingStep={setIsCancelBookingStep}
      onCheckInClick={onCheckInClick}
      isFullscreen={isFullscreen}
      isActivityPane={isActivityPane}
    />
  );
};
